import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type { OfferParty, TurnOwner } from "@/hooks/useCollaborations";

/**
 * Whose move it is, said from the viewer's point of view.
 *
 * This is the column that makes the inbox a daily habit. "NEGOTIATING" tells
 * you what state a row is in; "waiting on you" tells you whether to open it —
 * and an agency reconstructing that by hand for a client call is the problem
 * this whole screen exists to solve.
 *
 * Rendered as the viewer's turn or the other side's, never as an abstract
 * party name, because "waiting on the brand" is ambiguous when you are one.
 *
 * The dot drifts rather than pulsing. A pulse reads as an alert and would make
 * every open collaboration feel overdue; a slow drift reads as "alive".
 */
export default function TurnBadge({
  turnOwner,
  viewerParty,
  size = "sm",
}: {
  turnOwner: TurnOwner;
  viewerParty: OfferParty;
  size?: "sm" | "md";
}) {
  const { t } = useTranslation();

  const isMine = turnOwner === viewerParty;
  const isDone = turnOwner === "NOBODY";

  const label = isDone
    ? t("marketplace.collaboration.turn.nobody")
    : isMine
      ? t("marketplace.collaboration.turn.you")
      : viewerParty === "BRAND"
        ? t("marketplace.collaboration.turn.creator")
        : t("marketplace.collaboration.turn.brand");

  const tone = isDone
    ? { pill: "bg-success-bg text-success-fg border-success-border", dot: "bg-success-fg" }
    : isMine
      ? { pill: "bg-steel-50 text-steel-600 border-steel-100", dot: "bg-steel-600" }
      : { pill: "bg-neutral-100 text-neutral-500 border-transparent", dot: "bg-neutral-400" };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap rounded-pill border font-semibold",
        tone.pill,
        size === "md" ? "px-3 py-1.5 text-meta" : "px-2.5 py-1 text-micro",
      )}
    >
      <span
        aria-hidden
        className={cn("size-1.5 shrink-0 rounded-full", tone.dot, !isDone && "animate-drift")}
      />
      {label}
    </span>
  );
}
